import axios from 'axios';
import { decrypt_value } from '@/utils/web-crypto-fixed.util';
import cookies from '@/mixins/cookies.mixin';
import accountsFilter from '../data/global-filters';
import { clearSelectedAccountFilter, getSelectedAccountId } from '@/utils/global-filters.util';
import { currentAppPath, rememberReturnPath } from '@/utils/return-path.util';
import { get_tencrypt_key } from '@/utils/tencrypt.util';

const client = axios.create({
	headers: {
		'content-type': 'application/json',
	}
});

const shouldBypassAuth = (config) => {
	const currentAuthHeader = (config.headers?.Authorization || config.headers?.authorization || '').toString();
	const isBasicAuth = /^Basic\s+/i.test(currentAuthHeader);
	const isAuthEndpoint = config.url === '/_/signin' || config.url === '/_/send-recovery-password';
	return isBasicAuth || isAuthEndpoint;
};

const getTokenFromCookie = async (key) => {
	const encryptedCookie = cookies.methods._getCookie('_tid');
	if (encryptedCookie && key) {
		const decryptedToken = await decrypt_value(encryptedCookie, key) || '';
		return decryptedToken.replace(/^"(.*)"$/, '$1');
	}
	return '';
};

const parseInvitationPayload = (decryptedQuery) => {
	try {
		return JSON.parse(decryptedQuery);
	} catch {
		return JSON.parse(JSON.parse(decryptedQuery));
	}
};

const getTokenFromQuery = async (key) => {
	const encryptedQuery = new URLSearchParams(window.location.search).get('_v');
	if (!encryptedQuery || !key) {
		return '';
	}
	try {
		const decryptedQuery = await decrypt_value(encryptedQuery, key);
		const queryData = parseInvitationPayload(decryptedQuery);
		return queryData?.token || '';
	} catch {
		return '';
	}
};

client.interceptors.request.use(async (config) => {
	if (shouldBypassAuth(config)) {
		return config;
	}
	const tencryptKey = get_tencrypt_key();
	const invitationToken = await getTokenFromQuery(tencryptKey);
	const token = invitationToken || await getTokenFromCookie(tencryptKey);

	if (token) {
		config.headers.authorization = token;
	}

	if (accountsFilter.includes(config.url)) {
		const accountId = getSelectedAccountId();
		if (accountId !== undefined) {
			config.params.account_id = accountId;
		}
	}
	return config;
});

client.interceptors.response.use(undefined, error => {
	const public_routes = new Set([
		'/login',
		'/update-password',
		'/invitation/sign-up',
	]);

	const status = error?.response?.status;
	const status_code = error?.response?.data?.statusCode;
	const message = error?.response?.data?.message;
	const server_restarting = !error?.response || [502, 503, 504].includes(status);

	if (
		import.meta.env.DEV
		&& server_restarting
	) {
		return Promise.reject(error);
	}

	if (
		(
			status === 401
			|| status_code === 401
			|| (status_code === 422 && message === 'JWT malformed.')
		)
		&& !public_routes.has(window.location.pathname)
	) {
		rememberReturnPath(currentAppPath());
		clearSelectedAccountFilter();
		logout();
		cookies.methods._deleteCookie('_tid');
		window.location.href = '/login';
	}
	return Promise.reject(error);
});

const login = (credential) => {
	return client.post('/_/signin', { credential });
};

const loginWithPassword = (email, password) => {
	const credentials = Buffer.from(`${email}:${password}`).toString('base64');
	return client.post('/_/signin', {}, {
		headers: {
			Authorization: `Basic ${credentials}`,
		},
	});
};

const recoveryPassword = (data) => {
	return client.post('/_/send-recovery-password', {}, {
		headers: {
			Authorization: 'Basic ' + Buffer.from(data.email + ':' + data.password).toString('base64')
		}
	});
};

const logout = () => {
	return axios.get('/_/logout');
};

/**
 * Returns the same JWT string used for API requests (for Socket.IO auth).
 */
export async function getAuthToken() {
	const tencryptKey = get_tencrypt_key();
	return getTokenFromCookie(tencryptKey);
}

const api = {
	client,
	login,
	loginWithPassword,
	logout,
	recoveryPassword,
	getAuthToken,
};

export { api };
export default api;